import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Plus, ChevronUp, ChevronDown, Pencil, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABEL = { trade: 'Trade', internal: 'Internal' };

const UsageCell = ({ d, suffix = '' }) => (
  <div className="text-[11px] text-[#A3A3A3] leading-relaxed" data-testid={`dept-usage-${d.id}${suffix}`}>
    <span className="text-white">{d.active_service_types}</span> active service type{d.active_service_types === 1 ? '' : 's'}
    {d.service_types !== d.active_service_types && <> ({d.service_types} total)</>}
    {' · '}{d.jobs} job{d.jobs === 1 ? '' : 's'} · {d.visits} visit{d.visits === 1 ? '' : 's'}
    {d.type !== 'internal' && <><br />Inventory: {d.inventory_items} items · {d.inventory_categories} categories · {d.loadout_templates} loadouts</>}
  </div>
);

const DeptRow = ({ d, idx, total, onMove, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(d.name);
  useEffect(() => { setName(d.name); }, [d.name]);
  return (
    <div data-testid={`dept-row-${d.id}`} className={`grid grid-cols-[auto_1fr_130px_auto_auto] md:grid-cols-[auto_1fr_130px_1.4fr_auto] gap-3 items-center px-3 py-3 border-t border-[#262626] ${d.active === false ? 'opacity-50' : ''}`}>
      <div className="flex flex-col">
        <button data-testid={`dept-up-${d.id}`} disabled={idx === 0} onClick={() => onMove(idx, -1)} className="text-[#A3A3A3] hover:text-white disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
        <button data-testid={`dept-down-${d.id}`} disabled={idx === total - 1} onClick={() => onMove(idx, 1)} className="text-[#A3A3A3] hover:text-white disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
      </div>
      {editing ? (
        <div className="flex items-center gap-1.5">
          <Input data-testid={`dept-name-input-${d.id}`} value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white h-9" />
          <button data-testid={`dept-name-save-${d.id}`} onClick={async () => { if (name.trim() && name.trim() !== d.name && await onSave(d, { name: name.trim() }, 'Renamed — linked inventory, categories and loadouts updated')) setEditing(false); }} className="p-1.5 text-green-400 hover:bg-[#262626] rounded"><Check className="w-4 h-4" /></button>
          <button onClick={() => { setName(d.name); setEditing(false); }} className="p-1.5 text-[#A3A3A3] hover:bg-[#262626] rounded"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button data-testid={`dept-name-${d.id}`} onClick={() => setEditing(true)} className="text-left text-white font-semibold inline-flex items-center gap-2 hover:text-[#D4AF37]">
          {d.name} <Pencil className="w-3 h-3 text-[#525252]" />
          {d.active === false && <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] border border-[#3f3f3f] rounded px-1">Inactive</span>}
        </button>
      )}
      <select data-testid={`dept-type-${d.id}`} value={d.type} onChange={(e) => onSave(d, { type: e.target.value }, 'Type updated')} className="h-9 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2">
        <option value="trade">Trade</option>
        <option value="internal">Internal</option>
      </select>
      <div className="hidden md:block"><UsageCell d={d} /></div>
      <Button data-testid={`dept-toggle-active-${d.id}`} size="sm" variant="outline" onClick={() => onSave(d, { active: d.active === false }, d.active === false ? 'Reactivated' : 'Deactivated')} className={d.active === false ? 'border-green-700 text-green-400 hover:bg-green-900/30' : 'border-[#7F1D1D] text-red-400 hover:bg-[#7F1D1D] hover:text-white'}>
        {d.active === false ? 'Reactivate' : 'Deactivate'}
      </Button>
      <div className="md:hidden col-span-5 pl-7"><UsageCell d={d} suffix="-m" /></div>
    </div>
  );
};

export default function AdminDepartments() {
  const [rows, setRows] = useState([]);
  const [unassigned, setUnassigned] = useState(0);
  const [showInactive, setShowInactive] = useState(true);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('trade');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/departments/usage`, { withCredentials: true });
      setRows(data.departments); setUnassigned(data.unassigned_service_types);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load departments'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (d, patch, msg) => {
    try {
      await axios.put(`${API}/inv/departments/${d.id}`, patch, { withCredentials: true });
      toast.success(msg); await load(); return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); return false; }
  };
  const create = async () => {
    if (!newName.trim()) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/inv/departments`, { name: newName.trim(), type: newType }, { withCredentials: true });
      toast.success(`Added "${newName.trim()}"`); setNewName(''); setNewType('trade'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  const visible = rows.filter((d) => showInactive || d.active !== false);
  const move = async (idx, dir) => {
    const next = [...visible];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    try {
      await Promise.all(next.map((d, i) => (d.sort_order !== i ? axios.put(`${API}/inv/departments/${d.id}`, { sort_order: i }, { withCredentials: true }) : null)));
      load();
    } catch { toast.error('Reorder failed'); load(); }
  };

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2 flex items-center gap-2"><Building2 className="w-7 h-7 text-[#D4AF37]" /> Departments / Trades</h2>
        <p className="text-[#A3A3A3] text-sm max-w-3xl">
          The company-wide list of trades (Plumbing, HVAC, Electrical…) plus internal departments for non-customer work.
          New Shift picks a Department first, then its <Link to="/admin/service-types" className="text-[#D4AF37] hover:underline">Service Types</Link>.
          Inventory uses the same list (Trade departments only). This is separate from the Service / New Construction / Office division.
        </p>
        {unassigned > 0 && <p className="text-orange-300 text-xs mt-2" data-testid="dept-unassigned-note">{unassigned} service type(s) have no department yet — assign them on the Service Types page.</p>}
      </div>

      <div className="bg-[#141414] border border-[#D4AF37]/40 rounded-md p-4 space-y-2" data-testid="dept-add-card">
      <p className="text-white font-semibold text-sm">Add a Department / Trade</p>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_160px_auto] gap-3">
        <Input data-testid="dept-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} placeholder="New department (e.g. Electrical)" className="bg-[#0A0A0A] border-[#262626] text-white" />
        <select data-testid="dept-new-type" value={newType} onChange={(e) => setNewType(e.target.value)} className="h-10 rounded-md bg-[#0A0A0A] border border-[#262626] text-white text-sm px-2">
          <option value="trade">Trade</option>
          <option value="internal">Internal</option>
        </select>
        <Button data-testid="dept-create" onClick={create} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add Department</Button>
      </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-[#A3A3A3]">
        <input data-testid="dept-show-inactive" type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
      </label>

      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden" data-testid="dept-list">
        <div className="hidden md:grid grid-cols-[auto_1fr_130px_1.4fr_auto] gap-3 px-3 py-2 bg-[#0A0A0A] text-[#A3A3A3] text-xs uppercase tracking-wider">
          <span className="w-4" /><span>Name</span><span>Type</span><span>Usage</span><span />
        </div>
        {visible.length === 0 && <p className="p-6 text-center text-[#A3A3A3]">No departments.</p>}
        {visible.map((d, i) => <DeptRow key={d.id} d={d} idx={i} total={visible.length} onMove={move} onSave={save} />)}
      </div>
      <p className="text-[11px] text-[#525252]">Type: {Object.values(TYPE_LABEL).join(' / ')} — Internal departments are for PTO, training, meetings, shop/warehouse and office work and are hidden from Inventory dropdowns.</p>
    </div>
  );
}
